import React, { useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Avatar } from '../common/UIComponents';
import ChatWidget from '../chat/ChatWidget-refactored';
import DisclaimerFooter from '../common/DisclaimerFooter';
import NotificationBell from './NotificationBell';

// ── Protected Route ──────────────────────────────────────────────────────────
export function ProtectedRoute({ allowedRoles }) {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) return <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>Loading…</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

// ── App Shell ────────────────────────────────────────────────────────────────
export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = getNavItems(user?.role);
  const pageTitle = getPageTitle(location.pathname);

  const activePath = location.pathname.startsWith('/properties/')
    ? '/dashboard'
    : location.pathname.startsWith('/pros/')
    ? ''
    : location.pathname.startsWith('/projects/')
    ? '/projects'
    : location.pathname.startsWith('/insights')
    ? '/insights'
    : location.pathname === '/pro/profile'
    ? '/pro/profile'
    : location.pathname.startsWith('/pro/')
    ? '/pro'
    : location.pathname;

  const handleNav = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  return (
    <div className="app-shell">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="brand">
          <img src="/brand/svg/propatlas-mark.svg" className="brand-mark" alt="PropAtlas mark" />
          <div>
            <div className="brand-name">Prop<em>A</em>tlas</div>
            <div className="brand-sub">Property Redbook</div>
          </div>
        </div>

        <div className="persona-card">
          <div className="persona-label">{user?.role}</div>
          <div className="persona-name">{user?.first_name} {user?.last_name}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{user?.email}</div>
        </div>

        <nav className="nav">
          {navItems.map((item, i) =>
            item.section ? (
              <div key={i} className="nav-section">{item.section}</div>
            ) : (
              <button
                key={item.path}
                className={`nav-item ${activePath === item.path ? 'active' : ''}`}
                onClick={() => handleNav(item.path)}
              >
                <span style={{ width: 16, opacity: 0.7 }}>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            )
          )}
        </nav>

        <div className="sidebar-foot">
          <button
            onClick={() => handleNav('/settings')}
            className="btn ghost sm"
            style={{ width: '100%', justifyContent: 'center', marginBottom: 6 }}
          >
            ⚙ Settings
          </button>
          <button onClick={logout} className="btn ghost sm" style={{ width: '100%', justifyContent: 'center' }}>
            Sign out
          </button>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.08em', marginTop: 8, textAlign: 'center' }}>
            v1.0 · PropAtlas Registry
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="main">
        <div className="topbar">
          {/* Hamburger — mobile only (hidden via CSS on desktop) */}
          <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)} aria-label="Open menu">
            {sidebarOpen ? '✕' : '☰'}
          </button>

          {/* Brand name — mobile only */}
          <div className="topbar-brand" style={{ display: 'none' }}>
            <img src="/brand/svg/propatlas-mark.svg" className="brand-mark" alt="PropAtlas mark" style={{ width: 22 }} />
            <div className="brand-name" style={{ fontSize: 17 }}>Prop<em>A</em>tlas</div>
          </div>

          <div className="crumbs">
            <span>PropAtlas</span>
            <span style={{ margin: '0 6px', color: 'var(--ink-4)' }}>›</span>
            <strong>{pageTitle}</strong>
          </div>
          <div className="search">
            <div className="ico">🔍</div>
            <input placeholder="Search properties, records, pros…" />
          </div>
          <div className="topbar-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <NotificationBell />
            <Avatar initials={`${user?.first_name?.[0]}${user?.last_name?.[0]}`} size={32} />
          </div>
        </div>

        <Outlet />
        <DisclaimerFooter />
      </div>

      <ChatWidget />
    </div>
  );
}

function getNavItems(role) {
  switch (role) {
    case 'owner':
      return [
        { section: 'My Properties' },
        { path: '/dashboard', label: 'Properties', icon: '🏠' },
        { path: '/portfolio', label: 'Portfolio', icon: '📊' },
        { section: 'Development' },
        { path: '/projects', label: 'Projects', icon: '🏗' },
        { section: 'PropAtlas' },
        { path: '/eoi', label: 'Buy/EOI Market', icon: '🔍' },
        { path: '/marketplace', label: 'Marketplace', icon: '👥' },
        { path: '/bookings', label: 'Bookings', icon: '📅' },
        { path: '/insights', label: 'Market Insights', icon: '📰' },
      ];
    case 'agent':
      return [
        { section: 'Listings' },
        { path: '/dashboard', label: 'Properties', icon: '📊' },
        { path: '/portfolio', label: 'Portfolio', icon: '📈' },
        { section: 'Development' },
        { path: '/projects', label: 'Projects', icon: '🏗' },
        { section: 'PropAtlas' },
        { path: '/eoi', label: 'Buy/EOI Market', icon: '🔍' },
        { path: '/marketplace', label: 'Marketplace', icon: '👥' },
        { path: '/insights', label: 'Market Insights', icon: '📰' },
      ];
    case 'buyer':
      return [
        { section: 'Properties' },
        { path: '/dashboard', label: 'Shared With Me', icon: '🛡' },
        { section: 'Buy' },
        { path: '/eoi', label: 'My EOIs', icon: '🔍' },
        { path: '/insights', label: 'Market Insights', icon: '📰' },
      ];
    case 'pro':
      return [
        { section: 'Pro Tools' },
        { path: '/pro', label: 'Pro Dashboard', icon: '📊' },
        { path: '/pro/profile', label: 'My Profile', icon: '👤' },
        { path: '/bookings', label: 'Jobs', icon: '📅' },
        { path: '/marketplace', label: 'Browse', icon: '👥' },
        { section: 'Development' },
        { path: '/projects', label: 'Projects', icon: '🏗' },
        { path: '/insights', label: 'Market Insights', icon: '📰' },
      ];
    case 'admin':
      return [
        { section: 'Admin' },
        { path: '/admin', label: 'Dashboard', icon: '⚙️' },
      ];
    default:
      return [{ path: '/dashboard', label: 'Home', icon: '🏠' }];
  }
}

function getPageTitle(path) {
  if (path.startsWith('/properties/')) return 'Property Redbook';
  if (path.startsWith('/pros/')) return 'Pro Profile';
  if (path.startsWith('/projects/')) return 'Project';
  const map = {
    '/dashboard': 'Properties',
    '/marketplace': 'Marketplace',
    '/bookings': 'Bookings',
    '/pro/profile': 'My Profile',
    '/pro': 'Pro Dashboard',
    '/pro/profile': 'My Profile',
    '/eoi': 'Buy / EOI Marketplace',
    '/portfolio': 'Portfolio Dashboard',
    '/projects': 'Projects',
    '/insights': 'Market Insights',
    '/admin': 'Admin Dashboard',
    '/settings': 'Settings',
  };
  return map[path] || 'PropAtlas';
}
